import {
    Feedback,
    FeedbackComment,
    FeedbackCommentWithUser,
    FeedbackRequest,
    FeedbackRequestWithDetails,
    FeedbackWithDetails,
} from '@/common/types/databaseTypes';
import { authService } from '@/features/auth/services/authService';
import { supabase, Database } from '@tickitacka/shared';

export type FeedbackInsert = Database['public']['Tables']['feedback']['Insert'];
export interface CreateFeedbackParams {
    feedback: Omit<
        FeedbackInsert,
        'id' | 'register_date' | 'update_date' | 'enable'
    >;
    coreValueIds?: {
        positiveIds?: string[];
        negativeIds?: string[];
    };
    todoIds?: string[];
    notiParams: {
        workspaceName: string;
        workspaceImageUrl: string;
    };
}

export type FeedbackRequestInsert =
    Database['public']['Tables']['feedback_request']['Insert'];
export interface CreateFeedbackRequestParams {
    feedbackRequest: Omit<
        FeedbackRequestInsert,
        'id' | 'register_date' | 'update_date' | 'enable'
    >;
    todoIds?: string[];
    notiParams: {
        workspaceName: string;
        workspaceImageUrl: string;
    };
}

export interface UpdateFeedbackParams {
    id: string;
    fields: Partial<Feedback>;
    todoIds?: string[];
    coreValueIds?: {
        positiveIds?: string[];
        negativeIds?: string[];
    };
    notiParams: {
        workspaceName: string;
        workspaceImageUrl: string;
    };
}

export interface UpdateFeedbackRequestParams {
    id: string;
    fields: Partial<FeedbackRequest>;
    todoIds?: string[];
    notiParams: {
        workspaceName: string;
        workspaceImageUrl: string;
    };
}

export const feedbackService = {
    /**
     * 단일 피드백 데이터를 조회합니다.
     * @param feedbackId 피드백 ID
     * @returns 단일 피드백 데이터
     */
    async getFeedbackById(feedbackId: string): Promise<Feedback> {
        try {
            const { data, error } = await supabase
                .from('feedback')
                .select('*')
                .eq('id', feedbackId)
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(`Failed to fetch feedback ${feedbackId}`, error);
            throw error;
        }
    },

    /**
     * 사용자 관련 피드백 목록을 조회합니다
     * @param userId 사용자 ID
     * @param workspaceId 워크스페이스 ID
     * @param type 조회 타입 ('sender' 또는 'receiver')
     * @returns 피드백 목록
     */
    async getFeedbacksByUserId(
        userId: string,
        workspaceId: string,
        type: 'sender' | 'receiver'
    ): Promise<FeedbackWithDetails[]> {
        try {
            // 필드와 조건 설정
            const idField = type === 'sender' ? 'register_id' : 'receiver_id';
            const relationField =
                type === 'sender'
                    ? 'receiver:user!receiver_id'
                    : 'sender:user!register_id';

            const { data, error } = await supabase
                .from('feedback')
                .select(
                    `
                        *,
                        ${relationField} (
                            id,
                            name,
                            profile_image_url
                        ),
                        feedback_core_values:feedback_core_value(
                            id,
                            is_positive,
                            core_value:workspace_core_value(
                                id, 
                                name, 
                                enable
                            )
                        ),
                        feedback_todos:feedback_todo(
                            id,
                            todo:todo(
                                id,
                                title,
                                enable
                            )
                        ),
                        request:feedback_request!request_id(
                            id,
                            content,
                            enable
                        ),  
                        user_feedback_settings:user_feedback_setting!left(
                            user_id,
                            is_bookmarked,
                            is_hidden,
                            update_date
                        ),
                        emoji_reactions:feedback_emoji_reaction(
                            id,
                            emoji_code,
                            register_id,
                            register_date,
                            user:user!register_id(
                                id,
                                name,
                                profile_image_url
                            )
                        )
                    `
                )
                .eq(idField, userId)
                .eq('workspace_id', workspaceId)
                .eq('enable', true)
                .eq('feedback_request.enable', true)
                .eq('user_feedback_setting.user_id', userId)
                .order('register_date', { ascending: false });

            if (error) throw error;

            // 숨김 처리된 피드백을 필터링하고 is_bookmarked 정보 추가
            const filteredFeedbacks = (data || [])
                .filter(feedback => {
                    const userSetting = feedback.user_feedback_settings?.find(
                        setting => setting.user_id === userId
                    );
                    return !userSetting?.is_hidden;
                })
                .map(feedback => {
                    const userSetting = feedback.user_feedback_settings?.find(
                        setting => setting.user_id === userId
                    );
                    return {
                        ...feedback,
                        is_bookmarked: userSetting?.is_bookmarked || false,
                        bookmark_update_date: userSetting?.update_date,
                    };
                });

            // 각 피드백에 대한 댓글 목록 가져오기
            const feedbacksWithComments = await Promise.all(
                filteredFeedbacks.map(async feedback => {
                    const comments = await feedbackService.getFeedbackComments(
                        feedback.id
                    );
                    return {
                        ...feedback,
                        comments,
                    };
                })
            );

            return feedbacksWithComments;
        } catch (error) {
            console.error(
                `Failed to fetch detailed feedbacks for user ${userId} (${type})`,
                error
            );
            throw error;
        }
    },

    /**
     * 단일 피드백 요청 데이터를 조회합니다
     * @param feedbackRequestId 피드백 요청 ID
     * @returns 단일 피드백 요청 데이터
     */
    async getFeedbackRequestById(
        feedbackRequestId: string
    ): Promise<FeedbackRequest> {
        try {
            const { data, error } = await supabase
                .from('feedback_request')
                .select('*')
                .eq('id', feedbackRequestId)
                // .eq('enable', true)
                .maybeSingle();

            if (error) throw error;

            return data;
        } catch (error) {
            console.error(
                `Failed to fetch feedback request ${feedbackRequestId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 받은 피드백 요청 목록을 조회합니다
     * @param userId 사용자 ID
     * @param workspaceId 워크스페이스 ID
     * @returns 피드백 요청 목록
     */
    async getReceivedFeedbackRequests(
        userId: string,
        workspaceId: string
    ): Promise<FeedbackRequestWithDetails[]> {
        try {
            const { data, error } = await supabase
                .from('feedback_request')
                .select(
                    `
                        *,
                        sender:user!register_id (
                            id,
                            name,
                            profile_image_url
                        ),
                        feedback_request_todos:feedback_request_todo(
                            *,
                            todo:todo_id (
                                id,
                                title,
                                enable,
                                complete_date,
                                cancelled_date,
                                goal:goal_id (
                                    id,
                                    title,
                                    enable
                                )
                            )
                        )
                    `
                )
                .eq('receiver_id', userId)
                .eq('enable', true)
                .eq('is_rejected', false)
                .eq('is_completed', false)
                .eq('workspace_id', workspaceId)
                .order('register_date', { ascending: false });

            if (error) throw error;

            const feedbackRequestWithDetails =
                data?.map(feedbackRequest => ({
                    ...feedbackRequest,
                    feedback_request_todos:
                        feedbackRequest.feedback_request_todos?.map(item => ({
                            ...item,
                            todo: item.todo
                                ? {
                                      id: item.todo.id,
                                      title: item.todo.title,
                                      enable: item.todo.enable,
                                      complete_date: item.todo.complete_date,
                                      cancelled_date: item.todo.cancelled_date,
                                  }
                                : null,
                            goal: item.todo?.goal
                                ? {
                                      id: item.todo.goal.id,
                                      title: item.todo.goal.title,
                                      enable: item.todo.goal.enable,
                                  }
                                : null,
                        })) || [],
                })) || [];

            return feedbackRequestWithDetails as FeedbackRequestWithDetails[];
        } catch (error) {
            console.error(
                `Failed to fetch feedback requests received by user ${userId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 보낸 피드백 요청 목록을 조회합니다
     * @param userId 사용자 ID
     * @param workspaceId 워크스페이스 ID
     * @returns 피드백 요청 목록
     */
    async getSentFeedbackRequests(
        userId: string,
        workspaceId: string
    ): Promise<FeedbackRequestWithDetails[]> {
        try {
            const { data, error } = await supabase
                .from('feedback_request')
                .select(
                    `
                        *,
                        receiver:user!receiver_id (
                            id,
                            name,
                            profile_image_url
                        ),
                        feedback_request_todos:feedback_request_todo(
                            todo:todo_id (
                                id,
                                title,
                                enable
                            )
                        )
                    `
                )
                .eq('register_id', userId)
                .eq('enable', true)
                .eq('is_completed', false)
                .eq('workspace_id', workspaceId)
                .order('register_date', { ascending: false });

            if (error) throw error;

            const feedbackRequestWithDetails = data?.map(feedbackRequest => ({
                ...feedbackRequest,
                feedback_request_todos:
                    feedbackRequest.feedback_request_todos?.map(
                        feedbackRequestTodo => ({
                            ...feedbackRequestTodo,
                            todo: feedbackRequestTodo.todo
                                ? {
                                      id: feedbackRequestTodo.todo.id,
                                      title: feedbackRequestTodo.todo.title,
                                      enable: feedbackRequestTodo.todo.enable,
                                  }
                                : null,
                        })
                    ),
            }));

            return feedbackRequestWithDetails as FeedbackRequestWithDetails[];
        } catch (error) {
            console.error(
                `Failed to fetch feedback requests sent by user ${userId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 새 피드백을 생성합니다
     * @param feedbackParams 새 피드백 정보
     * @returns 생성된 피드백
     */
    async createFeedback(
        feedbackParams: CreateFeedbackParams
    ): Promise<Feedback> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { feedback, coreValueIds, todoIds } = feedbackParams;

            // 1. 피드백 테이블에 데이터 추가
            const { data, error } = await supabase
                .from('feedback')
                .insert({
                    ...feedback,
                    register_id: user.id,
                    register_date: new Date().toISOString(),
                    enable: true,
                    is_draft:
                        feedback.is_draft !== undefined
                            ? feedback.is_draft
                            : false,
                    is_read: false,
                })
                .select()
                .single();

            if (error) throw error;
            if (!data) throw new Error('Failed to create feedback');

            // 2. 선택된 핵심가치가 있으면 feedback_core_value 테이블에 데이터 추가
            if (coreValueIds) {
                const coreValueEntries = [];

                // 긍정적 핵심가치 추가
                if (
                    coreValueIds.positiveIds &&
                    coreValueIds.positiveIds.length > 0
                ) {
                    coreValueIds.positiveIds.forEach(coreValueId => {
                        coreValueEntries.push({
                            feedback_id: data.id,
                            core_value_id: coreValueId,
                            is_positive: true,
                            register_id: user.id,
                            register_date: new Date().toISOString(),
                            enable: true,
                        });
                    });
                }

                // 부정적 핵심가치 추가
                if (
                    coreValueIds.negativeIds &&
                    coreValueIds.negativeIds.length > 0
                ) {
                    coreValueIds.negativeIds.forEach(coreValueId => {
                        coreValueEntries.push({
                            feedback_id: data.id,
                            core_value_id: coreValueId,
                            is_positive: false,
                            register_id: user.id,
                            register_date: new Date().toISOString(),
                            enable: true,
                        });
                    });
                }

                if (coreValueEntries.length > 0) {
                    const { error: coreValueError } = await supabase
                        .from('feedback_core_value')
                        .insert(coreValueEntries);

                    if (coreValueError) {
                        console.error('핵심가치 연결 실패', coreValueError);
                    }
                }
            }

            // 3. 선택된 TODO가 있으면 feedback_todo 테이블에 데이터 추가
            if (todoIds && todoIds.length > 0) {
                const todoEntries = todoIds.map(todoId => ({
                    feedback_id: data.id,
                    todo_id: todoId,
                    register_id: user.id,
                    register_date: new Date().toISOString(),
                }));

                const { error: todoError } = await supabase
                    .from('feedback_todo')
                    .insert(todoEntries);

                if (todoError) {
                    console.error('투두 연결 실패', todoError);
                }
            }

            // 4. 요청에 대한 응답일 경우, 요청 데이터의 is_completed 업데이트
            if (feedback.request_id) {
                const { error: updateRequestError } = await supabase
                    .from('feedback_request')
                    .update({
                        is_completed: true,
                        update_id: user.id,
                        update_date: new Date().toISOString(),
                    })
                    .eq('id', feedback.request_id);
                if (updateRequestError) {
                    console.error(
                        '피드백 요청 업데이트 실패',
                        updateRequestError
                    );
                }
            }

            return data;
        } catch (error) {
            console.error('Failed to create feedback', error);
            throw error;
        }
    },

    /**
     * 피드백을 수정합니다
     * @param feedbackId 피드백 ID
     * @param fields 수정할 필드
     * @returns 수정된 피드백
     */
    async updateFeedback(params: UpdateFeedbackParams): Promise<Feedback> {
        const { id: feedbackId, fields, todoIds, coreValueIds } = params;

        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('feedback')
                .update({
                    ...fields,
                    // is_read: false, // 읽음 상태 초기화
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', feedbackId)
                .select()
                .single();

            if (error) throw error;

            if (todoIds) {
                const { error: todoDeleteError } = await supabase
                    .from('feedback_todo')
                    .delete()
                    .eq('feedback_id', feedbackId);
                if (todoDeleteError) throw todoDeleteError;

                const todoEntries = todoIds.map(todoId => ({
                    feedback_id: feedbackId,
                    todo_id: todoId,
                    register_id: user.id,
                    register_date: new Date().toISOString(),
                }));
                const { error: todoInsertError } = await supabase
                    .from('feedback_todo')
                    .insert(todoEntries);
                if (todoInsertError) throw todoInsertError;
            }

            if (coreValueIds) {
                // 기존 것 삭제
                const { error: coreValueDeleteError } = await supabase
                    .from('feedback_core_value')
                    .delete()
                    .eq('feedback_id', feedbackId);
                if (coreValueDeleteError) throw coreValueDeleteError;

                const coreValueEntries = [];
                // 긍정적 핵심가치 추가
                if (
                    coreValueIds.positiveIds &&
                    coreValueIds.positiveIds.length > 0
                ) {
                    coreValueIds.positiveIds.forEach(coreValueId => {
                        coreValueEntries.push({
                            feedback_id: feedbackId,
                            core_value_id: coreValueId,
                            is_positive: true,
                            register_id: user.id,
                            register_date: new Date().toISOString(),
                            enable: true,
                        });
                    });
                }

                // 부정적 핵심가치 추가
                if (
                    coreValueIds.negativeIds &&
                    coreValueIds.negativeIds.length > 0
                ) {
                    coreValueIds.negativeIds.forEach(coreValueId => {
                        coreValueEntries.push({
                            feedback_id: feedbackId,
                            core_value_id: coreValueId,
                            is_positive: false,
                            register_id: user.id,
                            register_date: new Date().toISOString(),
                            enable: true,
                        });
                    });
                }

                if (coreValueEntries.length > 0) {
                    const { error: coreValueError } = await supabase
                        .from('feedback_core_value')
                        .insert(coreValueEntries);

                    if (coreValueError) {
                        console.error('핵심가치 연결 실패', coreValueError);
                    }
                }
            }

            return data;
        } catch (error) {
            console.error(`Failed to update feedback ${feedbackId}`, error);
            throw error;
        }
    },

    /**
     * 피드백을 읽음 상태로 변경합니다(수신자)
     * @param feedbackId 피드백 ID
     * @returns 수정된 피드백
     */
    async markFeedbackAsRead(feedbackId: string): Promise<Feedback> {
        try {
            const { data, error } = await supabase
                .from('feedback')
                .update({
                    is_read: true,
                })
                .eq('id', feedbackId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(
                `Failed to mark feedback ${feedbackId} as read`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백을 자신의 리스트에서 숨김 처리합니다 (=삭제)
     * @param feedbackId 피드백 ID
     * @returns 숨김 처리된 피드백
     */
    async hideFeedbackForUser(feedbackId: string): Promise<Feedback> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            // 1. 피드백 정보 조회
            const { data: feedback, error: fetchError } = await supabase
                .from('feedback')
                .select('*')
                .eq('id', feedbackId)
                .single();

            if (fetchError) throw fetchError;
            if (!feedback) throw new Error('피드백을 찾을 수 없습니다.');

            // 2. 현재 사용자와 관련된 user_feedback_setting 생성 또는 업데이트
            const { error: settingError } = await supabase
                .from('user_feedback_setting')
                .upsert(
                    {
                        feedback_id: feedbackId,
                        user_id: user.id,
                        is_hidden: true,
                        update_date: new Date().toISOString(),
                    },
                    { onConflict: 'feedback_id, user_id' }
                );

            if (settingError) throw settingError;

            return feedback;
        } catch (error) {
            console.error(`Failed to hide feedback ${feedbackId}`, error);
            throw error;
        }
    },

    /**
     * 피드백을 북마크/해제 처리 합니다
     * @param feedbackId 피드백 ID
     * @param isBookmarked 북마크 여부 (기본값: true)
     * @returns 피드백 정보
     */
    async toggleFeedbackBookmark(
        feedbackId: string,
        isBookmarked: boolean
    ): Promise<Feedback> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            // 피드백 정보 조회
            const { data: feedback, error: fetchError } = await supabase
                .from('feedback')
                .select('*')
                .eq('id', feedbackId)
                .single();

            if (fetchError) throw fetchError;
            if (!feedback) throw new Error('피드백을 찾을 수 없습니다.');

            // 사용자와 관련된 user_feedback_setting 생성 또는 업데이트
            const { error: settingError } = await supabase
                .from('user_feedback_setting')
                .upsert(
                    {
                        feedback_id: feedbackId,
                        user_id: user.id,
                        is_bookmarked: isBookmarked,
                        update_date: new Date().toISOString(),
                    },
                    { onConflict: 'feedback_id, user_id' }
                );

            if (settingError) throw settingError;

            return feedback;
        } catch (error) {
            console.error(
                `Failed to ${
                    isBookmarked ? 'bookmark' : 'unbookmark'
                } feedback ${feedbackId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백에 코멘트를 추가합니다
     * @param feedbackId 피드백 ID
     * @param content 코멘트 내용
     * @param parentId 부모 코멘트 ID
     * @returns 생성된 코멘트
     */
    async addFeedbackComment(
        feedbackId: string,
        content: string,
        parentId?: string
    ): Promise<FeedbackComment> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('feedback_comment')
                .insert({
                    feedback_id: feedbackId,
                    content,
                    register_id: user.id,
                    parent_comment_id: parentId,
                    register_date: new Date().toISOString(),
                    enable: true,
                })
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(
                `Failed to add comment to feedback ${feedbackId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백 코멘트를 수정합니다
     * @param feedbackCommentId 피드백 코멘트 ID
     * @param content 코멘트 내용
     * @returns 수정된 코멘트
     */
    async updateFeedbackComment(
        feedbackCommentId: string,
        content: string
    ): Promise<FeedbackComment> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('feedback_comment')
                .update({
                    content,
                    update_date: new Date().toISOString(),
                    update_id: user.id,
                })
                .eq('id', feedbackCommentId)
                .select()
                .single();

            if (error) throw error;
            return data;
        } catch (error) {
            console.error(
                `Failed to update comment ${feedbackCommentId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백 코멘트를 삭제합니다
     */
    async deleteFeedbackComment(feedbackCommentId: string): Promise<void> {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error } = await supabase
                .from('feedback_comment')
                .update({
                    enable: false,
                    update_date: new Date().toISOString(),
                    update_id: user.id,
                })
                .eq('id', feedbackCommentId);

            if (error) throw error;
        } catch (error) {
            console.error(
                `Failed to delete comment ${feedbackCommentId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백의 코멘트 목록을 조회합니다
     * @param feedbackId 피드백 ID
     * @returns 코멘트 목록
     */
    async getFeedbackComments(
        feedbackId: string
    ): Promise<FeedbackCommentWithUser[]> {
        try {
            const { data, error } = await supabase
                .from('feedback_comment')
                .select(
                    `
                    *,
                    user:user!register_id (
                        id,
                        name,
                        profile_image_url
                    )
                `
                )
                .eq('feedback_id', feedbackId)
                .eq('enable', true)
                .order('register_date', { ascending: false });

            if (error) throw error;

            // 댓글과 답글 구조화
            const comments = (data as FeedbackCommentWithUser[]) || [];
            const rootComments: FeedbackCommentWithUser[] = [];
            const replyMap: Record<string, FeedbackCommentWithUser[]> = {};

            // 답글 맵 생성
            comments.forEach(comment => {
                if (comment.parent_comment_id) {
                    if (!replyMap[comment.parent_comment_id]) {
                        replyMap[comment.parent_comment_id] = [];
                    }
                    replyMap[comment.parent_comment_id].push(comment);
                } else {
                    rootComments.push(comment);
                }
            });

            // 답글 할당
            rootComments.forEach(comment => {
                if (replyMap[comment.id]) {
                    // 여기서 답글만 오름차순으로 정렬
                    replyMap[comment.id].sort(
                        (a, b) =>
                            new Date(a.register_date).getTime() -
                            new Date(b.register_date).getTime()
                    );
                    comment.children = replyMap[comment.id];
                }
            });

            return rootComments;
        } catch (error) {
            console.error(
                `Failed to fetch comments for feedback ${feedbackId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백 요청을 생성합니다
     * @param newFeedbackRequest 새 피드백 요청 정보
     * @returns 생성된 피드백 요청
     */
    async createFeedbackRequest(
        newFeedbackRequest: CreateFeedbackRequestParams
    ): Promise<FeedbackRequest> {
        const { feedbackRequest, todoIds } = newFeedbackRequest;

        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('feedback_request')
                .insert({
                    ...feedbackRequest,
                    register_id: user.id,
                    register_date: new Date().toISOString(),
                    enable: true,
                })
                .select()
                .single();

            if (error) throw error;
            if (!data) throw new Error('Failed to create feedback request');

            // 2. 선택된 TODO가 있으면 feedback_request_todo 테이블에 데이터 추가
            if (todoIds) {
                const todoEntries = [];
                todoIds.forEach(todoId => {
                    todoEntries.push({
                        request_id: data.id,
                        todo_id: todoId,
                        register_date: new Date().toISOString(),
                    });
                });

                const { error: todoError } = await supabase
                    .from('feedback_request_todo')
                    .insert(todoEntries);
                if (todoError) throw todoError;
            }

            return data;
        } catch (error) {
            console.error(`Failed to create feedback request`, error);
            throw error;
        }
    },

    /**
     * 피드백 요청을 수정합니다
     */
    async updateFeedbackRequest(params: UpdateFeedbackRequestParams) {
        const { id: feedbackRequestId, fields, todoIds } = params;

        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { data, error } = await supabase
                .from('feedback_request')
                .update({
                    ...fields,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', feedbackRequestId)
                .select()
                .single();

            if (error) throw error;
            if (!data) throw new Error('Failed to update feedback request');

            if (todoIds) {
                const { error: todoDeleteError } = await supabase
                    .from('feedback_request_todo')
                    .delete()
                    .eq('request_id', feedbackRequestId);
                if (todoDeleteError) throw todoDeleteError;

                const todoEntries = todoIds.map(todoId => ({
                    request_id: feedbackRequestId,
                    todo_id: todoId,
                    register_date: new Date().toISOString(),
                }));
                const { error: todoInsertError } = await supabase
                    .from('feedback_request_todo')
                    .insert(todoEntries);
                if (todoInsertError) throw todoInsertError;
            }

            return data;
        } catch (error) {
            console.error(
                `Failed to update feedback request ${feedbackRequestId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백 요청을 삭제합니다.
     */
    async deleteFeedbackRequest(feedbackRequestId: string) {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error } = await supabase
                .from('feedback_request')
                .update({
                    enable: false,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', feedbackRequestId);
            if (error) throw error;
        } catch (error) {
            console.error(
                `Failed to delete feedback request ${feedbackRequestId}`,
                error
            );
            throw error;
        }
    },

    /**
     * 피드백 요청을 거절합니다.
     */
    async rejectFeedbackRequest(feedbackRequestId: string) {
        try {
            const user = await authService.getCurrentUser();
            if (!user) throw new Error('사용자가 로그인되어 있지 않습니다.');

            const { error } = await supabase
                .from('feedback_request')
                .update({
                    is_rejected: true,
                    update_id: user.id,
                    update_date: new Date().toISOString(),
                })
                .eq('id', feedbackRequestId);
            if (error) throw error;
        } catch (error) {
            console.error(
                `Failed to reject feedback request ${feedbackRequestId}`,
                error
            );
            throw error;
        }
    },
};
